import { useState } from 'react';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Button } from '@/shared/ui/Button';
import type { ProfileStepData } from '../api/onboardingApi';
import { specialtyOptions } from '@/shared/constants/verticals';
import { useVertical } from '@/shared/hooks/useVertical';
import { PHONE_PLACEHOLDER, formatPhoneDisplay } from '@/shared/utils/validation';
import styles from './StepForm.module.css';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

interface ProfileStepProps {
  onSubmit: (data: ProfileStepData) => void;
  isLoading: boolean;
}

export function ProfileStep({ onSubmit, isLoading }: ProfileStepProps) {
  // Scoped to the practice's agency-set vertical — a medical practice must not be
  // offered dental specialties during onboarding.
  const { verticalId } = useVertical();
  const [form, setForm] = useState<ProfileStepData>({
    practiceName: '',
    specialty: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ProfileStepData, string>>>({});

  const handleChange = (field: keyof ProfileStepData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ProfileStepData, string>> = {};
    if (!form.practiceName) newErrors.practiceName = 'Practice name is required';
    if (!form.specialty) newErrors.specialty = 'Please select a specialty';
    if (!form.phone) newErrors.phone = 'Phone number is required';
    if (!form.address) newErrors.address = 'Address is required';
    if (!form.city) newErrors.city = 'City is required';
    if (!form.state) newErrors.state = 'State is required';
    if (!form.zip) newErrors.zip = 'ZIP code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.grid2}>
        <Input
          label="Practice Name"
          value={form.practiceName}
          onChange={(e) => handleChange('practiceName', e.target.value)}
          error={errors.practiceName}
          placeholder="Bright Smile Dental"
          fullWidth
        />
        <Select
          label="Specialty"
          value={form.specialty}
          onChange={(e) => handleChange('specialty', e.target.value)}
          options={specialtyOptions(verticalId)}
          placeholder="Select specialty"
          error={errors.specialty}
        />
      </div>
      <div className={styles.grid2}>
        <Input
          label="Phone Number"
          type="tel"
          value={form.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          onBlur={(e) => handleChange('phone', formatPhoneDisplay(e.target.value))}
          error={errors.phone}
          placeholder={PHONE_PLACEHOLDER}
          fullWidth
        />
        <Input
          label="Website (optional)"
          value={form.website}
          onChange={(e) => handleChange('website', e.target.value)}
          placeholder="https://yourpractice.com"
          fullWidth
        />
      </div>
      <Input
        label="Street Address"
        value={form.address}
        onChange={(e) => handleChange('address', e.target.value)}
        error={errors.address}
        placeholder="123 Main Street"
        fullWidth
      />
      <div className={styles.grid3}>
        <Input
          label="City"
          value={form.city}
          onChange={(e) => handleChange('city', e.target.value)}
          error={errors.city}
          placeholder="San Francisco"
          fullWidth
        />
        <Select
          label="State"
          value={form.state}
          onChange={(e) => handleChange('state', e.target.value)}
          options={US_STATES}
          placeholder="State"
          error={errors.state}
        />
        <Input
          label="ZIP Code"
          value={form.zip}
          onChange={(e) => handleChange('zip', e.target.value)}
          error={errors.zip}
          placeholder="94102"
          fullWidth
        />
      </div>
      <div className={styles.actions}>
        <Button type="submit" isLoading={isLoading} size="lg">
          Continue
        </Button>
      </div>
    </form>
  );
}
